import React, { useEffect, useState } from 'react';
import axios from '../axios-config';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings as SettingsIcon, Save, Plug, PlayCircle, CheckCircle2, XCircle, Clock, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const navigate = useNavigate();
  const [feePct, setFeePct] = useState(0.3);
  const [gstPct, setGstPct] = useState(18);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/settings`);
        setFeePct(data.success_fee_percentage ?? 0.3);
        setGstPct(data.gst_percentage ?? 18);
      } catch (e) { toast.error('Failed to load settings'); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { success_fee_percentage: Number(feePct), gst_percentage: Number(gstPct) });
      toast.success('Platform settings updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/admin-dashboard')} className="text-white/80 hover:text-white inline-flex items-center text-sm" data-testid="admin-settings-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </button>
          <div className="font-bold inline-flex items-center"><SettingsIcon className="h-5 w-5 text-gold mr-1.5" /> Platform Settings</div>
          <div className="w-12" />
        </nav>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-xl space-y-5">
        <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="admin-settings-form">
          <h3 className="text-sm font-bold text-navy uppercase tracking-wide">Success Fee Configuration</h3>

          <div>
            <label className="block text-xs font-bold text-navy mb-1.5">Success Fee Percentage (of Contract Value)</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="100" step="0.01" value={feePct} onChange={(e) => setFeePct(e.target.value)} className="w-32 px-3 py-2 rounded border border-input bg-background text-sm" data-testid="admin-settings-fee-input" />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Charged to the awarded supplier. Default: 0.30%</p>
          </div>

          <div>
            <label className="block text-xs font-bold text-navy mb-1.5">GST Percentage</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="100" step="0.01" value={gstPct} onChange={(e) => setGstPct(e.target.value)} className="w-32 px-3 py-2 rounded border border-input bg-background text-sm" data-testid="admin-settings-gst-input" />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Applied on top of the success fee. Default: 18%</p>
          </div>

          <button onClick={save} disabled={saving} className="w-full px-4 py-2.5 rounded bg-orange text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="admin-settings-save-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>

        <RazorpayIntegrationCard />
        <EmailIntegrationCard />
        <DataIsolationCard />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Admin → Data Isolation → Purge Test/Demo Data (Aug 2026)
// ─────────────────────────────────────────────────────────────
function DataIsolationCard() {
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null); // last dry-run or apply result

  const runScan = async (apply) => {
    if (apply) {
      if (!window.confirm('This will PERMANENTLY DELETE all test / demo / QA residue rows from the production database. This cannot be undone. Continue?')) return;
      setApplying(true);
    } else {
      setScanning(true);
    }
    try {
      const { data } = await axios.post(`${API}/admin/data-isolation/cleanup`, null, { params: { apply } });
      setReport(data);
      if (apply) toast.success(`Purged ${data.counters.total_deleted} residue row(s)`);
      else toast.success(`Scan complete — ${data.counters.total_residue_rows} residue row(s) found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Data isolation scan failed');
    } finally { setScanning(false); setApplying(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="admin-data-isolation-card">
      <h3 className="text-sm font-bold text-navy uppercase tracking-wide">Data Isolation — Purge Test / Demo Data</h3>
      <p className="text-[11px] text-muted-foreground">
        Removes test vendors / QA residue (unapproved, demo-flagged, or known test-prefix accounts and everything they created) from the production database. Real production users, buyers and suppliers are never touched.
      </p>

      <div className="flex gap-2">
        <button onClick={() => runScan(false)} disabled={scanning || applying} className="px-4 py-2 rounded border border-navy/40 text-navy text-sm font-bold disabled:opacity-50 inline-flex items-center" data-testid="data-isolation-scan-btn">
          <PlayCircle className="h-4 w-4 mr-1.5" /> {scanning ? 'Scanning…' : 'Scan (Dry-Run)'}
        </button>
        <button onClick={() => runScan(true)} disabled={scanning || applying || !report} className="px-4 py-2 rounded bg-red-600 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center" data-testid="data-isolation-apply-btn">
          <XCircle className="h-4 w-4 mr-1.5" /> {applying ? 'Purging…' : 'Purge Now'}
        </button>
      </div>

      {report && (
        <div className="border-t border-border pt-4" data-testid="data-isolation-report">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] mb-3">
            <Stat label="Mode" value={report.mode} testid="di-mode" />
            <Stat label="Real Prod Users" value={report.counters.real_prod_users} testid="di-real-users" />
            <Stat label="Residue Rows Found" value={report.counters.total_residue_rows} testid="di-residue-count" />
            <Stat label="Rows Deleted" value={report.counters.total_deleted} testid="di-deleted-count" />
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1">
            {report.collections.filter(s => s.residue_count > 0).map(s => (
              <div key={s.collection} className="flex items-center justify-between px-2.5 py-1.5 rounded bg-muted/40 border border-border text-[11px]" data-testid={`di-row-${s.collection}`}>
                <span className="font-mono">{s.collection}</span>
                <span className="font-bold text-navy">{s.residue_count}{s.deleted != null ? ` → deleted ${s.deleted}` : ''}</span>
              </div>
            ))}
            {report.collections.every(s => s.residue_count === 0) && (
              <div className="text-[11px] text-green-700 font-semibold px-1">No residue found — production data is clean.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Admin → Integrations → Razorpay  (maintenance utility)
// ─────────────────────────────────────────────────────────────
function RazorpayIntegrationCard() {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [eventType, setEventType] = useState('test.ping');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]); // newest first

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/integrations/razorpay/health`);
        setHealth(data);
      } catch (e) { /* non-fatal */ }
      finally { setHealthLoading(false); }
    })();
  }, []);

  const runProbe = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/integrations/razorpay/test-webhook`, { event_type: eventType });
      setResults(r => [data, ...r].slice(0, 10));
      if (data.ok) toast.success(`Webhook probe OK · HTTP ${data.status_code}`);
      else toast.error(`Probe failed${data.status_code ? ' · HTTP ' + data.status_code : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Probe failed');
    } finally { setRunning(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="admin-razorpay-integration-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-navy uppercase tracking-wide inline-flex items-center">
          <Plug className="h-4 w-4 mr-2 text-orange" /> Integrations → Razorpay
        </h3>
        <ModeBadge mode={health?.mode} loading={healthLoading} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Stat label="Mode" value={health?.mode ?? '—'} testid="rzp-health-mode" />
        <Stat label="Keys Present" value={health?.keys_present ? 'Yes' : 'No'} testid="rzp-health-keys" />
        <Stat label="Webhook Secret" value={health?.webhook_secret_configured ? 'Configured' : 'Missing'} testid="rzp-health-webhook-secret" />
        <Stat label="Key ID" value={health?.razorpay_key_id_prefix ?? '—'} testid="rzp-health-key-prefix" mono />
      </div>

      <div className="border-t border-border pt-4">
        <div className="text-[11px] text-muted-foreground mb-2">
          Send a <strong>synthetic</strong> webhook event signed with the configured secret. Synthetic events never trigger membership, success-fee, PO, invoice or email side-effects.
        </div>
        <div className="flex gap-2 items-stretch">
          <select value={eventType} onChange={e => setEventType(e.target.value)} className="flex-1 px-3 py-2 rounded border border-input bg-background text-sm" data-testid="rzp-probe-event-select">
            <option value="test.ping">test.ping (reachability)</option>
            <option value="test.signature_check">test.signature_check (HMAC)</option>
            <option value="test.replay_simulation">test.replay_simulation</option>
            <option value="test.audit_check">test.audit_check</option>
          </select>
          <button onClick={runProbe} disabled={running} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-50 inline-flex items-center" data-testid="rzp-run-probe-btn">
            <PlayCircle className="h-4 w-4 mr-1.5" /> {running ? 'Running…' : 'Run Probe'}
          </button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="border-t border-border pt-4">
          <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide mb-2">Recent Probes</div>
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="rzp-probe-results">
            {results.map((r, i) => <ResultRow key={r.event_id || i} r={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}

const ModeBadge = ({ mode, loading }) => {
  if (loading) return <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-muted text-muted-foreground">Loading</span>;
  const colour = mode === 'live' ? 'bg-green-100 text-green-800'
    : mode === 'test' ? 'bg-yellow-100 text-yellow-800'
    : 'bg-muted text-muted-foreground';
  return <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${colour}`} data-testid="rzp-mode-badge">{mode || 'disabled'}</span>;
};

const Stat = ({ label, value, testid, mono }) => (
  <div className="p-2 rounded bg-muted/40 border border-border" data-testid={testid}>
    <div className="text-[9px] uppercase font-bold text-muted-foreground tracking-wide">{label}</div>
    <div className={`text-xs font-bold text-navy ${mono ? 'font-mono' : ''}`}>{value}</div>
  </div>
);

const ResultRow = ({ r }) => (
  <div className={`p-2.5 rounded border text-[11px] ${r.ok ? 'border-green-300 bg-green-50/40' : 'border-red-300 bg-red-50/40'}`} data-testid={`rzp-result-${r.event_id || 'na'}`}>
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center font-bold">
        {r.ok ? <CheckCircle2 className="h-3 w-3 mr-1 text-green-700" /> : <XCircle className="h-3 w-3 mr-1 text-red-700" />}
        {r.result} · {r.event_type} · HTTP {r.status_code ?? '—'}
      </span>
      <span className="text-muted-foreground inline-flex items-center"><Clock className="h-3 w-3 mr-1" />{new Date(r.timestamp).toLocaleString()}</span>
    </div>
    <div className="mt-1 text-muted-foreground font-mono text-[10px] truncate">
      sig:{r.signature_accepted ? '✓' : '✗'} · audit:{r.audit_logged ? '✓' : '✗'} · {r.response_ms ?? '?'}ms · {r.event_id || r.error || ''}
    </div>
  </div>
);


// ─────────────────────────────────────────────────────────────
// Admin → Integrations → Email  (Resend health + test send)
// ─────────────────────────────────────────────────────────────
function EmailIntegrationCard() {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/integrations/email/health`);
        setHealth(data);
        if (!recipient && data?.alert_recipients?.[0]) setRecipient(data.alert_recipients[0]);
      } catch (e) { /* non-fatal */ }
      finally { setHealthLoading(false); }
    })();
  }, []);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/integrations/email/test`, recipient ? { to: recipient } : {});
      setResults(r => [data, ...r].slice(0, 10));
      if (data.ok) toast.success(`Test email queued to ${data.recipient}`);
      else if (data.delivery_status === 'Not Configured') toast.warning('Resend is not configured (RESEND_API_KEY missing)');
      else toast.error(data.error || 'Email send failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email test failed');
    } finally { setSending(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="admin-email-integration-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-navy uppercase tracking-wide inline-flex items-center">
          <Mail className="h-4 w-4 mr-2 text-orange" /> Integrations → Email (Resend)
        </h3>
        <EmailBadge enabled={health?.enabled} loading={healthLoading} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Stat label="Resend Enabled" value={health?.enabled ? 'Yes' : 'No'} testid="email-health-enabled" />
        <Stat label="API Key" value={health?.api_key_configured ? 'Configured' : 'Missing'} testid="email-health-apikey" />
        <Stat label="Sender" value={health?.sender ?? '—'} testid="email-health-sender" mono />
        <Stat label="Alert Recipients" value={(health?.alert_recipients || []).join(', ') || '—'} testid="email-health-alerts" mono />
      </div>

      <div className="border-t border-border pt-4">
        <div className="text-[11px] text-muted-foreground mb-2">
          Send a synthetic test email. Use it to verify <strong>SPF / DKIM / DMARC</strong> alignment in production before customer flows depend on email delivery.
        </div>
        <div className="flex gap-2 items-stretch">
          <input
            type="email"
            value={recipient}
            onChange={e => setRecipient(e.target.value)}
            placeholder="recipient@example.com"
            className="flex-1 px-3 py-2 rounded border border-input bg-background text-sm"
            data-testid="email-test-recipient-input"
          />
          <button onClick={send} disabled={sending} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-50 inline-flex items-center" data-testid="email-test-send-btn">
            <Send className="h-4 w-4 mr-1.5" /> {sending ? 'Sending…' : 'Send Test'}
          </button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="border-t border-border pt-4">
          <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide mb-2">Recent Sends</div>
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="email-results">
            {results.map((r, i) => <EmailResultRow key={r.message_id || r.timestamp + i} r={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}

const EmailBadge = ({ enabled, loading }) => {
  if (loading) return <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-muted text-muted-foreground">Loading</span>;
  const colour = enabled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  return <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${colour}`} data-testid="email-mode-badge">{enabled ? 'Live' : 'Not Configured'}</span>;
};

const EmailResultRow = ({ r }) => (
  <div className={`p-2.5 rounded border text-[11px] ${r.ok ? 'border-green-300 bg-green-50/40' : 'border-red-300 bg-red-50/40'}`} data-testid={`email-result-${r.message_id || 'na'}`}>
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center font-bold">
        {r.ok ? <CheckCircle2 className="h-3 w-3 mr-1 text-green-700" /> : <XCircle className="h-3 w-3 mr-1 text-red-700" />}
        {r.delivery_status} · {r.recipient} · HTTP {r.response_code ?? '—'}
      </span>
      <span className="text-muted-foreground inline-flex items-center"><Clock className="h-3 w-3 mr-1" />{new Date(r.timestamp).toLocaleString()}</span>
    </div>
    <div className="mt-1 text-muted-foreground font-mono text-[10px] truncate">
      {r.message_id ? `id:${r.message_id}` : (r.error || '')} · {r.response_ms ?? 0}ms
    </div>
  </div>
);
